import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import Icon from '../../components/Icon';
import ConfirmDialog from '../../components/ConfirmDialog';
import { AddRow, Chip, EmptyNote, KbdHint, ProgressLine } from '../../components/ui';
import { Goal, SubGoal, Tag, Todo, goalService } from '../../services/goalService';
import { AIPlanDraftListItem, deleteAIPlanDraft, getAIPlanDrafts } from '../../services/aiPlanService';
import { useTranslation } from '../../i18n';
import { useToast } from '../../contexts/ToastContext';
import { useTimers } from '../../contexts/TimerContext';
import { getErrorMessage } from '../../types/error';
import { formatDate, formatDurationShort } from '../../utils/format';
import { todayKey } from '../../utils/zonedTime';
import { logger } from '../../utils/logger';
import GoalStage, { RowActions } from './GoalStage';
import {
  GoalFilter,
  filterGoals,
  findStage,
  goalNumbers,
  moveItem,
  orderPayload,
  parseBoardHash,
  stageFolded,
  stageRows,
} from './boardData';
import './GoalsBoard.css';

export interface GoalsBoardProps {
  goals: Goal[];
  loading: boolean;
  error: string | null;
  loadGoals: () => Promise<void>;
  updateGoal: (goalId: string, updates: Partial<Goal>) => Promise<void>;
  deleteGoal: (goalId: string) => Promise<void>;
  toggleFavorite: (goalId: string) => Promise<void>;
  addSubGoal: (goalId: string, title: string, description: string) => Promise<void>;
  updateSubGoal: (goalId: string, subGoalId: string, updates: Partial<SubGoal>) => Promise<void>;
  deleteSubGoal: (goalId: string, subGoalId: string) => Promise<void>;
  updateTodo: (subGoalId: string, todoId: string, data: Partial<Todo>) => Promise<void>;
  onNewGoal: () => void;
  /** Opens a saved AI draft on the new goal page. */
  onOpenDraft: (draftId: string) => void;
  /** The page's address; under the task drawer, the page's and not the task's (T-183). */
  hash?: string;
}

const useHash = (): string => {
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const on = () => setHash(window.location.hash);
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return hash;
};

const WIDE = '(min-width: 1100px)';
const useWide = (): boolean => {
  const query = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(WIDE) : null;
  const [wide, setWide] = useState(() => (query ? query.matches : true));
  useEffect(() => {
    if (!query) return undefined;
    const on = () => setWide(query.matches);
    query.addEventListener?.('change', on);
    return () => query.removeEventListener?.('change', on);
  }, [query]);
  return wide;
};

/** Local changes shown before the reloaded tree arrives: field patches, new orders, deletions. */
interface Overlay {
  patch: Record<string, Partial<Todo>>;
  order: Record<string, string[]>;
  gone: string[];
}
const EMPTY: Overlay = { patch: {}, order: {}, gone: [] };

/** The goal with [ov] applied; every task also learns its stage id (the tree may omit it). */
const applyOverlay = (goal: Goal, ov: Overlay): Goal => {
  const all = new Map<string, Todo>();
  for (const s of goal.subGoals ?? []) for (const td of s.todos ?? []) all.set(td.id, { ...td, ...ov.patch[td.id] });
  const claimed = new Set(Object.values(ov.order).flat());
  const gone = new Set(ov.gone);
  return {
    ...goal,
    subGoals: (goal.subGoals ?? []).map((s) => {
      const ids = ov.order[s.id];
      const todos = ids
        ? ids.flatMap((id, i) => {
            const td = all.get(id);
            return td ? [{ ...td, subGoalId: s.id, orderIndex: i }] : [];
          })
        : (s.todos ?? []).filter((td) => !claimed.has(td.id)).map((td) => ({ ...(all.get(td.id) as Todo), subGoalId: s.id }));
      return { ...s, todos: todos.filter((td) => !gone.has(td.id)) };
    }),
  };
};

/** A value safe inside a quoted attribute selector. */
const attr = (value: string): string => value.replace(/["\\]/g, '\\$&');

const isTyping = (el: EventTarget | null): boolean => {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
};

/**
 * Hedefler (Faz 23, T-182; w-hedefler.html): the goal list beside the chosen goal. The goal's
 * stages are panels of task rows with columns (task, time, date, tag); tasks are added,
 * renamed, dated and tagged in place and dragged within or between stages. One page for
 * #/goals, #/goals/:id, #/subgoals/:id/todos (scrolls to the stage) and #/ai-drafts (Taslaklar).
 */
const GoalsBoard: React.FC<GoalsBoardProps> = (props) => {
  const { goals, loading, error, loadGoals, onNewGoal, onOpenDraft } = props;
  const { t, language } = useTranslation();
  const ownHash = useHash();
  const hash = props.hash ?? ownHash;
  const openTodoId = ownHash.match(/^#\/subgoals\/[^/]+\/todos\/([^/?]+)$/)?.[1] ?? null;
  const route = useMemo(() => parseBoardHash(hash), [hash]);
  const wide = useWide();
  const today = todayKey();

  const routed = route.goalId
    ? goals.find((g) => g.id === route.goalId) ?? null
    : route.stageId
      ? findStage(goals, route.stageId)?.goal ?? null
      : null;
  const [filter, setFilter] = useState<GoalFilter>(() => (route.drafts ? 'drafts' : 'active'));
  useEffect(() => {
    if (route.drafts) setFilter('drafts');
  }, [route.drafts]);
  // A finished goal opened by link shows under Biten
  useEffect(() => {
    if (routed?.completed) setFilter((f) => (f === 'active' ? 'done' : f));
  }, [routed?.id, routed?.completed]);

  const listed = useMemo(() => filterGoals(goals, filter, route.tagId), [goals, filter, route.tagId]);
  const selected = routed ?? (wide && filter !== 'drafts' ? listed[0] ?? null : null);
  const showDetail = wide || Boolean(routed);
  const showList = wide || !routed;
  const tagQuery = route.tagId ? `?tag=${encodeURIComponent(route.tagId)}` : '';

  const tagName = useMemo(() => {
    if (!route.tagId) return null;
    for (const g of goals) for (const s of g.subGoals ?? []) for (const td of s.todos ?? []) {
      const tag = td.tags?.find((x) => x.id === route.tagId);
      if (tag) return tag;
    }
    return null;
  }, [goals, route.tagId]);

  const activeCount = goals.filter((g) => !g.completed).length;
  const doneCount = goals.filter((g) => g.completed).length;

  return (
    <div className={`goals-board${showList && showDetail ? ' is-split' : ''}`}>
      {showList && (
        <section className="gb-list" aria-label={t('hedefler.listLabel')}>
          <div className="gb-list__head">
            <h1>{t('navigation.goals')}</h1>
            <button type="button" className="ui-btn ui-btn--small" onClick={onNewGoal}>
              <Icon name="plus" aria-hidden="true" />
              {t('hedefler.newGoal')}
            </button>
          </div>
          <div className="gb-list__chips" role="group" aria-label={t('goals.filter.label')}>
            <Chip label={t('hedefler.filterActive')} count={activeCount} selected={filter === 'active'} onClick={() => setFilter('active')} />
            <Chip label={t('hedefler.filterFavorites')} selected={filter === 'favorites'} onClick={() => setFilter('favorites')} />
            <Chip label={t('hedefler.filterDone')} count={doneCount} selected={filter === 'done'} onClick={() => setFilter('done')} />
            <Chip label={t('hedefler.filterDrafts')} selected={filter === 'drafts'} onClick={() => setFilter('drafts')} />
          </div>
          {route.tagId && (
            <div className="gb-list__tag">
              <span className="gb-tag-dot" style={{ background: tagName?.color }} aria-hidden="true" />
              <span>{t('hedefler.tagFilter', { name: tagName?.name ?? '…' })}</span>
              <a href="#/goals" aria-label={t('hedefler.clearTag')}>
                <Icon name="close" aria-hidden="true" />
              </a>
            </div>
          )}
          {error && (
            <div className="gb-list__error" role="alert">
              <span>{error}</span>
              <button type="button" className="ui-btn ui-btn--small" onClick={() => { void loadGoals(); }}>
                {t('common.retry')}
              </button>
            </div>
          )}
          {filter === 'drafts' ? (
            <DraftList onView={onOpenDraft} />
          ) : loading && goals.length === 0 ? (
            <div className="gb-list__loading" aria-busy="true">{t('common.loading')}</div>
          ) : listed.length === 0 ? (
            <p className="gb-list__empty">{goals.length === 0 ? t('goals.noGoalsSubtitle') : t('hedefler.emptyFilter')}</p>
          ) : (
            <div className="gb-goals">
              {listed.map((g) => {
                const n = goalNumbers(g, today);
                return (
                  <a
                    key={g.id}
                    href={`#/goals/${g.id}${tagQuery}`}
                    className="gb-goal"
                    aria-current={selected?.id === g.id ? 'true' : undefined}
                  >
                    <span className="gb-goal__line">
                      <span className="gb-goal__title">
                        {g.isFavorite && <Icon name="star" className="gb-goal__star" aria-hidden="true" />}
                        <bdi>{g.title}</bdi>
                      </span>
                      <span className="gb-goal__count">{n.done}/{n.total}</span>
                    </span>
                    <span className="gb-goal__meta">
                      {g.dueDate ? formatDate(g.dueDate, language, { day: 'numeric', month: 'short', year: 'numeric' }) : t('hedefler.noDate')}
                      {n.today > 0 && <> · {t('hedefler.todayCount', { count: n.today })}</>}
                      {n.late > 0 && <> · <span className="is-late">{t('hedefler.lateCount', { count: n.late })}</span></>}
                    </span>
                    <ProgressLine value={n.total ? n.done / n.total : 0} done={g.completed} />
                  </a>
                );
              })}
            </div>
          )}
        </section>
      )}
      {showDetail && (
        <main className="gb-detail" aria-label={selected?.title ?? t('navigation.goals')}>
          {!wide && (
            <a className="gb-back" href={`#/goals${tagQuery}`}>
              <Icon name="chevronLeft" aria-hidden="true" />
              {t('navigation.goals')}
            </a>
          )}
          {selected ? (
            <GoalDetail key={selected.id} {...props} goal={selected} focusStageId={route.stageId} tagFilter={route.tagId} openTodoId={openTodoId} />
          ) : loading && goals.length === 0 ? null : (
            <div className="gb-detail__empty">
              <EmptyNote
                title={goals.length === 0 ? t('goals.noGoals') : t('hedefler.pickGoal')}
                description={goals.length === 0 ? t('goals.noGoalsSubtitle') : undefined}
                action={
                  <button type="button" className="ui-btn ui-btn--primary" onClick={onNewGoal}>
                    <Icon name="plus" aria-hidden="true" /> {t('hedefler.newGoal')}
                  </button>
                }
              />
            </div>
          )}
        </main>
      )}
    </div>
  );
};

/** Taslaklar: AI plans not made into goals yet; open one in the review, or delete it. */
const DraftList: React.FC<{ onView: (draftId: string) => void }> = ({ onView }) => {
  const { t, language } = useTranslation();
  const [drafts, setDrafts] = useState<AIPlanDraftListItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirm, setConfirm] = useState<AIPlanDraftListItem | null>(null);

  useEffect(() => {
    let live = true;
    getAIPlanDrafts()
      .then((d) => live && setDrafts(d))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  const view = (id: string) => onView(id);

  if (failed) return <p className="gb-list__empty" role="alert">{t('aiDrafts.errors.loadFailed')}</p>;
  if (!drafts) return <div className="gb-list__loading" aria-busy="true">{t('common.loading')}</div>;
  if (drafts.length === 0) {
    return <p className="gb-list__empty">{t('aiDrafts.empty.subtitle')}</p>;
  }
  return (
    <div className="gb-goals">
      {drafts.map((d) => (
        <div key={d.id} className="gb-draft">
          <button type="button" className="gb-draft__open" onClick={() => view(d.id)}>
            <span className="gb-goal__title"><bdi>{d.title}</bdi></span>
            <span className="gb-goal__meta">
              {t('hedefler.draftMeta', { count: d.sub_goals_count, date: formatDate(d.created_at, language, { day: 'numeric', month: 'short' }) })}
            </span>
          </button>
          <button type="button" className="gb-cellbtn" aria-label={`${t('aiDrafts.actions.delete')}: ${d.title}`} onClick={() => setConfirm(d)}>
            <Icon name="trash" aria-hidden="true" />
          </button>
        </div>
      ))}
      <ConfirmDialog
        isOpen={!!confirm}
        title={t('aiDrafts.deleteConfirm.title')}
        message={t('aiDrafts.deleteConfirm.message', { title: confirm?.title ?? '' })}
        confirmText={t('aiDrafts.actions.delete')}
        cancelText={t('common.cancel')}
        onConfirm={async () => {
          if (!confirm) return;
          const id = confirm.id;
          setConfirm(null);
          try {
            await deleteAIPlanDraft(id);
            setDrafts((list) => (list ?? []).filter((x) => x.id !== id));
          } catch {
            setFailed(true);
          }
        }}
        onCancel={() => setConfirm(null)}
        type="danger"
      />
    </div>
  );
};

type DetailProps = GoalsBoardProps & { goal: Goal; focusStageId: string | null; tagFilter: string | null; openTodoId: string | null };

const GoalDetail: React.FC<DetailProps> = ({
  goal: sourceGoal,
  focusStageId,
  tagFilter,
  openTodoId,
  loadGoals,
  updateGoal,
  deleteGoal,
  toggleFavorite,
  addSubGoal,
  updateSubGoal,
  deleteSubGoal,
  updateTodo,
}) => {
  const { t, language } = useTranslation();
  const toast = useToast();
  const timers = useTimers();
  const today = todayKey();
  const [overlay, setOverlay] = useState<Overlay>(EMPTY);
  useEffect(() => setOverlay(EMPTY), [sourceGoal]);
  const goal = useMemo(() => applyOverlay(sourceGoal, overlay), [sourceGoal, overlay]);
  const stages = useMemo(() => goal.subGoals ?? [], [goal]);
  const n = goalNumbers(goal, today);

  const [fold, setFold] = useState<Record<string, boolean>>({});
  const isFolded = (s: SubGoal) => fold[s.id] ?? (tagFilter ? false : stageFolded(s));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingStage, setAddingStage] = useState<string | null>(null);
  const [addingNewStage, setAddingNewStage] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirm, setConfirm] = useState<{ kind: 'goal' } | { kind: 'stage'; stage: SubGoal } | { kind: 'todo'; todo: Todo } | null>(null);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [highlight, setHighlight] = useState<string | null>(null);
  const refocus = useRef<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    goalService.getTags().then((tags) => live && setAllTags(tags)).catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  // #/subgoals/:id/todos opens here: unfold that stage, bring it into view, mark it a moment
  useEffect(() => {
    if (!focusStageId) return undefined;
    setFold((f) => ({ ...f, [focusStageId]: false }));
    setHighlight(focusStageId);
    const frame = window.requestAnimationFrame(() => {
      document.querySelector(`[data-stage-id="${attr(focusStageId)}"]`)?.scrollIntoView?.({ block: 'start' });
    });
    const timer = window.setTimeout(() => setHighlight(null), 2000);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [focusStageId]);

  useEffect(() => {
    if (!refocus.current) return;
    document.querySelector<HTMLElement>(`[data-todo-id="${attr(refocus.current)}"]`)?.focus();
    refocus.current = null;
  });

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [menuOpen]);

  const fail = useCallback(
    (err: unknown, key: string) => {
      logger.error(key, err);
      toast.error(getErrorMessage(err, t(key)));
      setOverlay(EMPTY);
      void loadGoals();
    },
    [toast, t, loadGoals]
  );

  const patch = (id: string, p: Partial<Todo>) =>
    setOverlay((o) => ({ ...o, patch: { ...o.patch, [id]: { ...o.patch[id], ...p } } }));

  const firstStage = () => stages.find((s) => !isFolded(s)) ?? stages.find((s) => !s.completed) ?? stages[0];

  const openAdd = (stage?: SubGoal) => {
    const s = stage ?? firstStage();
    if (!s) {
      setAddingNewStage(true);
      return;
    }
    setFold((f) => ({ ...f, [s.id]: false }));
    setAddingStage(s.id);
  };

  const runningTodo = useMemo(() => {
    for (const s of stages) for (const td of s.todos ?? []) if (td.isStarted && td.startedAt && !td.completed) return td;
    return null;
  }, [stages]);

  const timer = async (todo: Todo) => {
    try {
      if (todo.isStarted && todo.startedAt) {
        await timers.stopTimer(todo.id, { startedAt: todo.startedAt, onCorrected: () => { void loadGoals(); } });
      } else {
        await goalService.startTodo(todo.id);
        await timers.refresh();
      }
      await loadGoals();
    } catch (err) {
      fail(err, todo.isStarted ? 'errors.stopTodo' : 'errors.startTodo');
    }
  };

  /** The head's Zamanlayıcı: stops the running task, else times the focused or the next one. */
  const headTimer = () => {
    if (runningTodo) {
      void timer(runningTodo);
      return;
    }
    const focusedId = (document.activeElement as HTMLElement | null)?.closest?.('[data-todo-id]')?.getAttribute('data-todo-id');
    const open = stages.flatMap((s) => stageRows(s).open);
    const pick =
      open.find((td) => td.id === focusedId) ?? open.find((td) => td.dueDate === today) ?? open[0];
    if (pick) void timer(pick);
    else toast.info(t('hedefler.nothingToTime'));
  };

  const reorder = async (stageId: string, open: Todo[], done: Todo[]) => {
    setOverlay((o) => ({ ...o, order: { ...o.order, [stageId]: [...open, ...done].map((x) => x.id) } }));
    try {
      await goalService.reorderTodos(stageId, orderPayload(open, done));
      await loadGoals();
    } catch (err) {
      fail(err, 'errors.reorderTodos');
    }
  };

  /** Moves [todo] into [stageId] at [index] of its open tasks (the end when omitted). */
  const moveTo = async (todo: Todo, stageId: string, index?: number, quiet = false) => {
    const from = stages.find((s) => s.todos?.some((x) => x.id === todo.id));
    const to = stages.find((s) => s.id === stageId);
    if (!from || !to || from.id === to.id) return;
    const src = stageRows(from);
    const dst = stageRows(to);
    const open = [...dst.open];
    open.splice(index ?? open.length, 0, todo);
    setOverlay((o) => ({
      ...o,
      order: {
        ...o.order,
        [from.id]: [...src.open, ...src.done].filter((x) => x.id !== todo.id).map((x) => x.id),
        [to.id]: [...open, ...dst.done].map((x) => x.id),
      },
    }));
    try {
      await goalService.moveTodo(todo.id, to.id);
      if (index !== undefined) await goalService.reorderTodos(to.id, orderPayload(open, dst.done));
      await loadGoals();
      if (!quiet) {
        toast.success(t('hedefler.moved', { stage: to.title }), {
          actions: [
            {
              label: t('common.undo'),
              onClick: () => {
                goalService.moveTodo(todo.id, from.id).then(() => loadGoals()).catch((err) => fail(err, 'errors.moveTodo'));
              },
            },
          ],
        });
      }
    } catch (err) {
      fail(err, 'errors.moveTodo');
    }
  };

  const toggle = async (todo: Todo) => {
    const completing = !todo.completed;
    patch(todo.id, { completed: completing, completedAt: completing ? new Date().toISOString() : null, isStarted: false });
    try {
      await goalService.toggleTodoCompletion(todo.id);
      if (todo.isStarted) void timers.refresh();
      await loadGoals();
      if (completing) {
        toast.success(t('todos.toast.completed'), {
          actions: [
            {
              label: t('common.undo'),
              onClick: () => {
                goalService.toggleTodoCompletion(todo.id).then(() => loadGoals()).catch((err) => fail(err, 'errors.toggleTodo'));
              },
            },
          ],
        });
      }
    } catch (err) {
      fail(err, 'errors.toggleTodo');
    }
  };

  const actions: RowActions = {
    toggle: (todo) => { void toggle(todo); },
    rename: (todo, title) => {
      patch(todo.id, { title });
      updateTodo(todo.subGoalId, todo.id, { title }).catch(() => setOverlay(EMPTY));
    },
    setDue: (todo, due) => {
      patch(todo.id, { dueDate: due });
      updateTodo(todo.subGoalId, todo.id, { dueDate: due }).catch(() => setOverlay(EMPTY));
    },
    setTags: (todo, ids) => {
      patch(todo.id, { tags: allTags.filter((x) => ids.includes(x.id)) });
      goalService
        .setTodoTags(todo.id, ids)
        .then(() => loadGoals())
        .catch((err) => fail(err, 'tags.saveFailed'));
    },
    timer: (todo) => { void timer(todo); },
    open: (todo) => {
      window.location.hash = `#/subgoals/${todo.subGoalId}/todos/${todo.id}`;
    },
    remove: (todo) => setConfirm({ kind: 'todo', todo }),
    moveTo: (todo, stageId) => { void moveTo(todo, stageId); },
    shift: (todo, delta) => {
      const stage = stages.find((s) => s.id === todo.subGoalId);
      if (!stage) return;
      const { open, done } = stageRows(stage);
      const at = open.findIndex((x) => x.id === todo.id);
      const to = at + delta;
      if (at < 0 || to < 0 || to >= open.length) return;
      refocus.current = todo.id;
      void reorder(stage.id, moveItem(open, at, to), done);
    },
  };

  const addTask = async (stage: SubGoal, title: string) => {
    try {
      const created = await goalService.createTodo({ subGoalId: stage.id, title, orderIndex: stageRows(stage).open.length });
      if (tagFilter) await goalService.setTodoTags(created.id, [tagFilter]);
      await loadGoals();
    } catch (err) {
      fail(err, 'errors.addTodo');
    }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const fromId = active.data.current?.stageId as string | undefined;
    const toId = over.data.current?.stageId as string | undefined;
    const from = stages.find((s) => s.id === fromId);
    const to = stages.find((s) => s.id === toId);
    if (!from || !to) return;
    const todo = (from.todos ?? []).find((x) => x.id === active.id);
    if (!todo) return;
    const dst = stageRows(to);
    const overIndex = dst.open.findIndex((x) => x.id === over.id);
    if (from.id === to.id) {
      const at = dst.open.findIndex((x) => x.id === todo.id);
      if (at < 0 || overIndex < 0) return;
      void reorder(to.id, moveItem(dst.open, at, overIndex), dst.done);
    } else {
      void moveTo(todo, to.id, overIndex < 0 ? dst.open.length : overIndex);
    }
  };

  // N: a new task in the focused row's stage, or the first open stage
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'n' || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      if (document.querySelector('.modal-overlay, [role="dialog"]')) return;
      e.preventDefault();
      const stageId = (document.activeElement as HTMLElement | null)?.closest?.('[data-stage-id]')?.getAttribute('data-stage-id');
      openAdd(stages.find((s) => s.id === stageId));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const [titleDraft, setTitleDraft] = useState(goal.title);
  const [descDraft, setDescDraft] = useState(goal.description ?? '');
  const saveTitle = () => {
    setEditingTitle(false);
    const title = titleDraft.trim();
    if (title && title !== goal.title) void updateGoal(goal.id, { title }).catch(() => undefined);
  };
  const saveDesc = () => {
    setEditingDesc(false);
    if (descDraft.trim() !== (goal.description ?? '').trim()) {
      void updateGoal(goal.id, { description: descDraft.trim() }).catch(() => undefined);
    }
  };

  const sendSummary = async () => {
    try {
      await goalService.sendGoalSummary(goal.id);
      toast.success(t('goalDetailPage.actions.summarySent'));
    } catch {
      toast.error(t('goalDetailPage.actions.summaryFailed'));
    }
  };

  const completeGoal = async () => {
    const completing = !goal.completed;
    try {
      await updateGoal(goal.id, { completed: completing });
      if (completing) {
        toast.success(t('progress.goalCompleted'), {
          actions: [{ label: t('common.undo'), onClick: () => { void updateGoal(goal.id, { completed: false }).catch(() => undefined); } }],
        });
      }
    } catch {
      // useGoals reported it
    }
  };

  const confirmText = (() => {
    if (!confirm) return null;
    if (confirm.kind === 'goal') return { title: t('goalCard.confirm.deleteGoal.title'), message: t('goalCard.confirm.deleteGoal.message') };
    if (confirm.kind === 'stage') return { title: t('goalCard.confirm.deleteSubGoal.title'), message: t('goalCard.confirm.deleteSubGoal.message') };
    return { title: t('hedefler.deleteTaskTitle'), message: t('hedefler.deleteTaskMessage', { title: confirm.todo.title }) };
  })();

  const onConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    try {
      if (c.kind === 'goal') {
        await deleteGoal(goal.id);
        window.location.hash = '#/goals';
      } else if (c.kind === 'stage') {
        await deleteSubGoal(goal.id, c.stage.id);
      } else {
        setOverlay((o) => ({ ...o, gone: [...o.gone, c.todo.id] }));
        await goalService.deleteTodoIn(c.todo.subGoalId, c.todo.id);
        await loadGoals();
      }
    } catch (err) {
      if (c.kind === 'todo') fail(err, 'errors.deleteTodo');
    }
  };

  const dueText = goal.dueDate ? formatDate(goal.dueDate, language, { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  return (
    <div className="gb-goal-detail">
      <header className="gb-head">
        <div className="gb-head__text">
          <span className="gb-head__crumb">
            {t('navigation.goals')} › <bdi>{goal.title}</bdi>
          </span>
          <div className="gb-head__title-row">
            {editingTitle ? (
              <input
                className="gb-head__title-input"
                autoFocus
                aria-label={t('hedefler.goalTitle')}
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveTitle();
                  if (e.key === 'Escape') {
                    setTitleDraft(goal.title);
                    setEditingTitle(false);
                  }
                }}
              />
            ) : (
              <h2>
                <button
                  type="button"
                  className="gb-head__title"
                  title={t('hedefler.editHint')}
                  onClick={() => {
                    setTitleDraft(goal.title);
                    setEditingTitle(true);
                  }}
                >
                  <bdi>{goal.title}</bdi>
                </button>
              </h2>
            )}
            <button
              type="button"
              className="gb-cellbtn gb-head__fav"
              aria-pressed={Boolean(goal.isFavorite)}
              aria-label={t(goal.isFavorite ? 'hedefler.unfavorite' : 'hedefler.favorite')}
              onClick={() => { void toggleFavorite(goal.id).catch(() => undefined); }}
            >
              <Icon name={goal.isFavorite ? 'star' : 'starOutline'} aria-hidden="true" />
            </button>
            {goal.completed && <span className="gb-stage__badge">{t('goalDetailPage.status.completed')}</span>}
          </div>
          {editingDesc ? (
            <textarea
              className="gb-head__desc-input"
              autoFocus
              rows={2}
              aria-label={t('hedefler.description')}
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onBlur={saveDesc}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setDescDraft(goal.description ?? '');
                  setEditingDesc(false);
                }
              }}
            />
          ) : (
            <button
              type="button"
              className={`gb-head__desc${goal.description ? '' : ' is-empty'}`}
              onClick={() => {
                setDescDraft(goal.description ?? '');
                setEditingDesc(true);
              }}
            >
              {goal.description ? <bdi>{goal.description}</bdi> : t('hedefler.addDescription')}
            </button>
          )}
        </div>
        <div className="gb-head__actions">
          <button type="button" className="ui-btn ui-btn--primary" onClick={() => openAdd()}>
            <Icon name="plus" aria-hidden="true" />
            {t('hedefler.task')}
          </button>
          <button type="button" className="ui-btn" onClick={() => setAddingNewStage(true)}>
            {t('hedefler.addStage')}
          </button>
          <button type="button" className={`ui-btn${runningTodo ? ' is-live' : ''}`} onClick={headTimer}>
            <Icon name={runningTodo ? 'pause' : 'play'} aria-hidden="true" />
            {runningTodo ? t('hedefler.stopTimer') : t('hedefler.timer')}
          </button>
          <div className="gb-pop" ref={menuRef}>
            <button
              type="button"
              className="ui-btn gb-head__more"
              aria-label={t('goalDetailPage.actions.more')}
              aria-haspopup="true"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <Icon name="more" aria-hidden="true" />
            </button>
            {menuOpen && (
              <div className="gb-pop__panel gb-pop__panel--end gb-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); void completeGoal(); }}>
                  <Icon name={goal.completed ? 'undo' : 'circleCheck'} aria-hidden="true" />{' '}
                  {goal.completed ? t('hedefler.reopenGoal') : t('hedefler.completeGoal')}
                </button>
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); void sendSummary(); }}>
                  <Icon name="mail" aria-hidden="true" /> {t('goalDetailPage.actions.emailSummary')}
                </button>
                <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenuOpen(false); setConfirm({ kind: 'goal' }); }}>
                  <Icon name="trash" aria-hidden="true" /> {t('goalDetailPage.actions.delete')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="gb-stats">
        <span>{t('hedefler.statTasks', { done: n.done, total: n.total })}</span>
        <span>{t('hedefler.statStages', { count: n.stages })}</span>
        <label className="gb-stats__due">
          <span className="sr-only">{t('hedefler.dueDate')}</span>
          <Icon name="calendar" aria-hidden="true" />
          <input
            type="date"
            value={goal.dueDate ? goal.dueDate.slice(0, 10) : ''}
            aria-label={t('hedefler.dueDate')}
            onChange={(e) => { void updateGoal(goal.id, { dueDate: e.target.value }).catch(() => undefined); }}
          />
          {dueText && n.daysLeft !== null && (
            <span className={n.daysLeft < 0 ? 'is-late' : undefined}>
              {n.daysLeft >= 0
                ? t('hedefler.statDaysLeft', { count: n.daysLeft })
                : t('hedefler.statDaysPast', { count: -n.daysLeft })}
            </span>
          )}
        </label>
        {n.workedSeconds > 0 && <span>{t('hedefler.statWorked', { time: formatDurationShort(n.workedSeconds, language) })}</span>}
        {n.remainingSeconds > 0 && <span>{t('hedefler.statRemaining', { time: formatDurationShort(n.remainingSeconds, language) })}</span>}
      </div>

      <div className="gb-content">
        {stages.length > 0 && (
          <div className="gb-grid gb-grid--head" aria-hidden="true">
            <span />
            <span>{t('hedefler.colTask')}</span>
            <span className="gb-col-time">{t('hedefler.colTime')}</span>
            <span className="gb-col-date">{t('hedefler.colDate')}</span>
            <span className="gb-col-tag">{t('hedefler.colTag')}</span>
            <span />
          </div>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          {stages.map((stage, i) => (
            <GoalStage
              key={stage.id}
              stage={stage}
              index={i}
              folded={isFolded(stage)}
              onFold={(f) => setFold((m) => ({ ...m, [stage.id]: f }))}
              stages={stages}
              today={today}
              allTags={allTags}
              actions={actions}
              editingId={editingId}
              setEditingId={setEditingId}
              adding={addingStage === stage.id}
              setAdding={(o) => setAddingStage(o ? stage.id : null)}
              onAdd={addTask}
              onRename={(s, title) => { void updateSubGoal(goal.id, s.id, { title }).catch(() => undefined); }}
              onToggleDone={(s) => { void updateSubGoal(goal.id, s.id, { completed: !s.completed }).catch(() => undefined); }}
              onDelete={(s) => setConfirm({ kind: 'stage', stage: s })}
              tagFilter={tagFilter}
              highlighted={highlight === stage.id}
              openTodoId={openTodoId}
            />
          ))}
        </DndContext>
        {stages.length === 0 && <p className="gb-stage__empty">{t('hedefler.noStages')}</p>}
        <div className="gb-addstage">
          <AddRow
            label={t('hedefler.addStage')}
            placeholder={t('hedefler.addStageHint')}
            open={addingNewStage}
            onOpenChange={setAddingNewStage}
            onSubmit={(title) => addSubGoal(goal.id, title, '').catch(() => undefined)}
          />
        </div>
        <KbdHint
          hints={[
            { keys: 'N', label: t('hedefler.kbdNew') },
            { keys: 'E', label: t('hedefler.kbdEdit') },
            { keys: '⌥↑↓', label: t('hedefler.kbdSort') },
            { keys: t('hedefler.kbdSpace'), label: t('hedefler.kbdDone') },
            { keys: 'T', label: t('hedefler.kbdTimer') },
          ]}
        />
      </div>

      <ConfirmDialog
        isOpen={!!confirm}
        title={confirmText?.title ?? ''}
        message={confirmText?.message ?? ''}
        confirmText={t('goalCard.confirm.deleteGoal.confirm')}
        cancelText={t('common.cancel')}
        onConfirm={() => { void onConfirm(); }}
        onCancel={() => setConfirm(null)}
        type="danger"
      />
    </div>
  );
};

export default GoalsBoard;
